import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import apiClient from "../services/api";
import { useAuth } from "../context/AuthContext";
import { Users, List, Building2, Truck, Database, ClipboardList, Fingerprint, BookUser } from "lucide-react";
import { Tabs, TabsContent } from "../components/ui/tabs";
import { UnderlineTabsList as TabsList, UnderlineTabsTrigger as TabsTrigger } from "../components/ui/tabs-variants";
import {
  UsersTab,
  MaterialsTab,
  EquipmentSettingsTab,
  ContractorsTab,
  SuppliersTab,
  OptionsTab,
  BidItemOptionsTab,
  MiscSettingsTab,
  BackupTab,
  PasskeysTab,
} from "../components/settings";
import { sortAlphabetically, sortMaterialsByCategory, sortStrings } from "../lib/sortUtils";
import ContactsSyncCard from "../components/contacts/ContactsSyncCard";
import PositionsCard from "../components/contacts/PositionsCard";
const SettingsPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Settings options
  const [materialUnits, setMaterialUnits] = useState([]);
  const [materialCategories, setMaterialCategories] = useState([]);
  const [generalContractors, setGeneralContractors] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [employeeRoles, setEmployeeRoles] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [billingCategories, setBillingCategories] = useState([]);
  const [certifiedPayrollOfficial, setCertifiedPayrollOfficial] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [equipmentUnits, setEquipmentUnits] = useState([]);
  const [equipmentCategories, setEquipmentCategories] = useState([]);
  const [defaultBurdenPercent, setDefaultBurdenPercent] = useState(28);
  const [defaultSalesTaxPercent, setDefaultSalesTaxPercent] = useState(8.25);
  const [miscItems, setMiscItems] = useState([]);
  const [miscUnits, setMiscUnits] = useState([]);
  const [billingOverdueDays, setBillingOverdueDays] = useState(30);
  const [addressHomeBase, setAddressHomeBase] = useState("");

  // Backup state
  const [backupHistory, setBackupHistory] = useState([]);

  // Role checks
  const canAccessBackup = user?.role === "admin" || user?.role === "project_manager" || user?.role === "office_manager";

  // Tab state synced with ?tab= query param (e.g. OneDrive OAuth callback -> ?tab=backup)
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = ["users", "bid-items", "contractors", "suppliers", "options", "passkeys"];
  if (canAccessBackup) validTabs.push("contacts", "backup");
  const requestedTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(validTabs.includes(requestedTab) ? requestedTab : "users");

  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("tab", value);
      return params;
    }, { replace: true });
  };

  const fetchBackupHistory = useCallback(async () => {
    try {
      const response = await apiClient.get(`/backup/history`);
      setBackupHistory(response.data.backups || []);
    } catch (error) {
      console.error("Failed to fetch backup history");
    }
  }, [getAuthHeaders]);

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, materialsRes, optionsRes] = await Promise.all([
        apiClient.get(`/users`),
        apiClient.get(`/materials`),
        apiClient.get(`/settings/options`),
      ]);
      setUsers(usersRes.data);
      setMaterials(materialsRes.data);
      setMaterialUnits(optionsRes.data.material_units || []);
      setMaterialCategories(optionsRes.data.material_categories || []);
      setGeneralContractors(optionsRes.data.general_contractors || []);
      setSuppliers(optionsRes.data.suppliers || []);
      setEmployeeRoles(optionsRes.data.employee_roles || []);
      setDocumentCategories(optionsRes.data.document_categories || []);
      setBillingCategories(optionsRes.data.billing_categories || []);
      setCertifiedPayrollOfficial(optionsRes.data.certified_payroll_official || null);
      setEquipment(optionsRes.data.equipment || []);
      setEquipmentUnits(optionsRes.data.equipment_units || []);
      setEquipmentCategories(optionsRes.data.equipment_categories || []);
      setDefaultBurdenPercent(optionsRes.data.default_burden_percent || 28);
      setDefaultSalesTaxPercent(optionsRes.data.default_sales_tax_percent || 8.25);
      setMiscItems(optionsRes.data.misc_items || []);
      setMiscUnits(optionsRes.data.misc_units || []);
      setBillingOverdueDays(optionsRes.data.billing_overdue_days || 30);
      setAddressHomeBase(optionsRes.data.address_home_base || "");
    } catch (error) {
      console.error("Failed to load data", error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
    if (canAccessBackup) {
      fetchBackupHistory();
    }
  }, [fetchData, fetchBackupHistory, canAccessBackup]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Sort lists using shared utilities
  const sortedMaterials = sortMaterialsByCategory(materials);
  const sortedEquipment = sortAlphabetically(equipment);
  const sortedGeneralContractors = sortAlphabetically(generalContractors);
  const sortedSuppliers = sortAlphabetically(suppliers);
  const sortedMaterialCategories = sortStrings(materialCategories);
  const sortedMaterialUnits = sortStrings(materialUnits);
  const sortedEquipmentUnits = sortStrings(equipmentUnits);
  const sortedEquipmentCategories = sortStrings(equipmentCategories);
  const sortedEmployeeRoles = sortStrings(employeeRoles);

  return (
    <div className="page-enter space-y-8" data-testid="settings-page">
      <div className="page-header">
        <h1 className="text-4xl font-bold tracking-tight text-foreground">Settings</h1>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList data-testid="settings-tabs">
          <TabsTrigger value="users" data-testid="users-tab">
            <Users className="w-4 h-4 mr-2" />Users
          </TabsTrigger>
          <TabsTrigger value="bid-items" data-testid="bid-items-tab">
            <ClipboardList className="w-4 h-4 mr-2" />Bid Items
          </TabsTrigger>
          <TabsTrigger value="contractors" data-testid="contractors-tab">
            <Building2 className="w-4 h-4 mr-2" />General Contractors
          </TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="suppliers-tab">
            <Truck className="w-4 h-4 mr-2" />Suppliers
          </TabsTrigger>
          <TabsTrigger value="options" data-testid="options-tab">
            <List className="w-4 h-4 mr-2" />Options
          </TabsTrigger>
          {canAccessBackup && (
            <TabsTrigger value="contacts" data-testid="contact-settings-tab">
              <BookUser className="w-4 h-4 mr-2" />Contacts
            </TabsTrigger>
          )}
          <TabsTrigger value="passkeys" data-testid="passkeys-tab">
            <Fingerprint className="w-4 h-4 mr-2" />Passkeys
          </TabsTrigger>
          {canAccessBackup && (
            <TabsTrigger value="backup" data-testid="backup-tab">
              <Database className="w-4 h-4 mr-2" />Backup
            </TabsTrigger>
          )}
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <UsersTab
            users={users}
            currentUser={user}
            getAuthHeaders={getAuthHeaders}
            onRefresh={fetchData}
          />
        </TabsContent>

        {/* Bid Items Tab (Materials, Equipment, Misc, Options) */}
        <TabsContent value="bid-items">
          <Tabs defaultValue="materials" className="space-y-4">
            <TabsList data-testid="settings-subtabs">
              <TabsTrigger value="materials" data-testid="materials-subtab">Materials</TabsTrigger>
              <TabsTrigger value="equipment" data-testid="equipment-subtab">Equipment</TabsTrigger>
              <TabsTrigger value="misc" data-testid="misc-subtab">Misc.</TabsTrigger>
              <TabsTrigger value="bid-options" data-testid="bid-options-subtab">Options</TabsTrigger>
            </TabsList>

            <TabsContent value="materials">
              <MaterialsTab
                materials={sortedMaterials}
                materialUnits={sortedMaterialUnits}
                materialCategories={sortedMaterialCategories}
                billingCategories={sortStrings(billingCategories)}
                getAuthHeaders={getAuthHeaders}
                onRefresh={fetchData}
              />
            </TabsContent>

            <TabsContent value="equipment">
              <EquipmentSettingsTab
                equipment={sortedEquipment}
                equipmentUnits={sortedEquipmentUnits}
                equipmentCategories={sortedEquipmentCategories}
                getAuthHeaders={getAuthHeaders}
                onRefresh={fetchData}
              />
            </TabsContent>

            <TabsContent value="misc">
              <MiscSettingsTab
                miscItems={miscItems}
                miscUnits={sortStrings(miscUnits)}
                billingCategories={sortStrings(billingCategories)}
                getAuthHeaders={getAuthHeaders}
                onRefresh={fetchData}
              />
            </TabsContent>

            <TabsContent value="bid-options">
              <BidItemOptionsTab
                materialUnits={sortedMaterialUnits}
                materialCategories={sortedMaterialCategories}
                equipmentUnits={sortedEquipmentUnits}
                equipmentCategories={sortedEquipmentCategories}
                miscUnits={sortStrings(miscUnits)}
                billingCategories={sortStrings(billingCategories)}
                getAuthHeaders={getAuthHeaders}
                onRefresh={fetchData}
              />
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* General Contractors Tab */}
        <TabsContent value="contractors">
          <ContractorsTab
            generalContractors={sortedGeneralContractors}
            getAuthHeaders={getAuthHeaders}
            onRefresh={fetchData}
          />
        </TabsContent>

        {/* Suppliers Tab */}
        <TabsContent value="suppliers">
          <SuppliersTab
            suppliers={sortedSuppliers}
            getAuthHeaders={getAuthHeaders}
            onRefresh={fetchData}
          />
        </TabsContent>

        {/* Options Tab */}
        <TabsContent value="options" className="space-y-6">
          <OptionsTab
            employeeRoles={sortedEmployeeRoles}
            documentCategories={documentCategories}
            certifiedPayrollOfficial={certifiedPayrollOfficial}
            defaultBurdenPercent={defaultBurdenPercent}
            defaultSalesTaxPercent={defaultSalesTaxPercent}
            billingOverdueDays={billingOverdueDays}
            addressHomeBase={addressHomeBase}
            userRole={user?.role}
            getAuthHeaders={getAuthHeaders}
            onRefresh={fetchData}
          />
        </TabsContent>

        {/* Passkeys Tab */}
        {canAccessBackup && (
          <TabsContent value="contacts" className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold">Contact Settings</h2>
              <p className="text-sm text-muted-foreground">Shared Contacts publish to everyone's Outlook address list (GAL) through Exchange Online. Manage the sync here.</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
              <ContactsSyncCard isAdmin={user?.role === "admin"} />
              <PositionsCard />
            </div>
          </TabsContent>
        )}

        <TabsContent value="passkeys">
          <PasskeysTab
            users={users}
            getAuthHeaders={getAuthHeaders}
            currentUser={user}
          />
        </TabsContent>

        {/* Backup Tab */}
        {canAccessBackup && (
          <TabsContent value="backup" className="space-y-6">
            <BackupTab
              backupHistory={backupHistory}
              getAuthHeaders={getAuthHeaders}
              onRefresh={fetchBackupHistory}
              onDataRefresh={fetchData}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default SettingsPage;
